import React, { SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useMutation, useQuery } from "react-query";
import { atom, useRecoilState } from "recoil";
import { IconButton, Paper, Tab, Tabs } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SettingsIcon from "@mui/icons-material/Settings";

import ResponsiveDialog from "../components/ResponsiveDialog";
import { Row } from "../Layout/utils";
import {
  deleteGroup,
  Filter,
  FilterGroup,
  getFavoriteGroup,
  listGroups,
} from "../services/Alert";
import {
  AlertOrdering,
  DATE_ORDERING,
  GranularityType,
} from "../services/AlertListFilterPayload";
import { AlertDragDrop } from "./AlertDragDrop";
import { ExportAlertMenu } from "./AlertExportMenu";
import { kpiIdOrder } from "./kpiIds";
import { getDefaultGroup } from "./Filter/utils";
import { FilterGroupCreate } from "./FilterGroup/FilterGroupCreate";
import { FilterGroupList } from "./FilterGroup/FilterGroupList";
import { FilterGroupName } from "./FilterGroup/FilterGroupName";
import { FilterGroupRow } from "./FilterGroup/FilterGroupRow";
import { FilterGroupSave } from "./FilterGroup/FilterGroupSave";
import { kpiColumnWidthAtom, kpiSelectedAtom } from "./atom";
import { ActionTooltip } from "../components/Tooltips";

export const granularityAtom = atom<GranularityType>({
  key: "granularity",
  default: 0,
});

export const orderingAtom = atom<AlertOrdering>({
  key: "AlertOrdering",
  default: DATE_ORDERING,
});

export const fullScreenAtom = atom<boolean>({
  key: "fullScreenTab",
  default: false,
});

export default function AlertGranularityTabs({
  skipDefaultGroup = false,
}: {
  skipDefaultGroup?: boolean;
}) {
  const intl = useIntl();
  const moduleSlug = "active_alerts";
  const [activeTab, setActiveTab] = useRecoilState(granularityAtom);
  const [kpiSelected, setKpiSelected] = useRecoilState(kpiSelectedAtom);
  const [kpiColumnWidth, setKpiColumnWidth] =
    useRecoilState(kpiColumnWidthAtom);
  const [open, setOpen] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = useState<FilterGroup | null>(null);
  const [savedColumnWidths, setSavedColumnWidths] = useState<
    Map<number, number>
  >(new Map());
  const [fullScreen, setFullScreen] = useRecoilState(fullScreenAtom);

  const deleteMutation = useMutation(
    (groupId: number) => deleteGroup(groupId),
    {
      onSuccess: (_) => {
        query.refetch().then();
      },
    }
  );

  const query = useQuery(["groups", "table"], () => listGroups("table"));

  useEffect(() => {
    if (query.data && !selectedGroup) {
      const favorite = getFavoriteGroup(query.data.results as any);
      const name = intl.formatMessage({
        id: "DEFAULT_PARAMETER_FILTER_GROUP_NAME",
      });
      const group =
        favorite || query.data.results[0] || getDefaultGroup(name, "table");
      setSelectedGroup(group);
      if (!skipDefaultGroup) {
        setKpiSelected(group.filters as unknown as kpiIdOrder[]);
      } else {
        // The URL only encodes ids + order, not widths. Capture the saved
        // group widths (by KPI id) so the effect below can restore them.
        const groupFilters = group.filters as unknown as kpiIdOrder[];
        setSavedColumnWidths(
          new Map(
            groupFilters
              .filter((f) => f.columnWidth !== undefined)
              .map((f) => [f.id, f.columnWidth as number])
          )
        );
      }
    }
  }, [
    query.data,
    selectedGroup,
    intl,
    setKpiSelected,
    skipDefaultGroup,
    setSavedColumnWidths,
  ]);

  // Restore saved column widths onto URL-driven columns (matched by KPI id).
  // No-ops once widths are in place (same reference returned) so it never loops.
  useEffect(() => {
    if (!skipDefaultGroup || savedColumnWidths.size === 0) return;
    setKpiSelected((current) => {
      let changed = false;
      const next = current.map((col) => {
        const saved = savedColumnWidths.get(col.id);
        if (col.columnWidth === undefined && saved !== undefined) {
          changed = true;
          return { ...col, columnWidth: saved };
        }
        return col;
      });
      return changed ? next : current;
    });
  }, [skipDefaultGroup, savedColumnWidths, kpiSelected, setKpiSelected]);

  useEffect(() => {
    setSelectedGroup((prevSelectedGroup) => {
      return prevSelectedGroup
        ? ({
            ...prevSelectedGroup,
            filters: kpiSelected.map((kpi) => ({
              ...kpi,
              columnWidth: kpiColumnWidth.get(kpi.order) || kpi.columnWidth,
            })) as unknown as Filter[],
          } as FilterGroup)
        : null;
    });
  }, [kpiSelected, kpiColumnWidth, setSelectedGroup]);

  const handleTabChange = useCallback(
    (event: SyntheticEvent<{}>, newValue: number) => {
      setActiveTab(newValue as GranularityType);
    },
    [setActiveTab]
  );

  const handleSelectedGroupChange = useCallback(
    (filterGroup: FilterGroup) => {
      setKpiColumnWidth(new Map<number, number>());
      setSelectedGroup(filterGroup);
      setKpiSelected(filterGroup.filters as unknown as kpiIdOrder[]);
    },
    [setSelectedGroup, setKpiSelected, setKpiColumnWidth]
  );

  const handleSelectedDelete = (group: FilterGroup) => {
    if (selectedGroup?.id === group.id) {
      setSelectedGroup(null);
    }
    deleteMutation.mutate(group.id);
  };

  const handleCreate = (group: FilterGroup) => {
    query.refetch().then(() => setSelectedGroup(group));
  };

  const saveGroup = (group: FilterGroup) => {
    query.refetch().then(() => setSelectedGroup(group));
  };

  const useGetLabel = () => {
    return [
      intl.formatMessage({ id: "RESULTAT_GRAIN_HEBERGEMENT" }),
      intl.formatMessage({ id: "RESULTAT_GRAIN_UH_TYPE" }),
      intl.formatMessage({ id: "RESULTAT_GRAIN_UH_CAT" }),
    ];
  };

  return (
    <Paper
      sx={(theme) => ({
        background: theme.vars.palette.filter.background,
        borderRadius: "12px 8px 0 0",
        "& .MuiTabs-root .MuiTabs-list": { gap: 0.25 },
        "& .MuiTabs-indicator": { visibility: "hidden" },
      })}
    >
      <Row
        sx={{
          gap: 0.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="icon tabs example"
          sx={{ display: "contents" }}
        >
          {useGetLabel().map((value, index) => (
            <Tab
              key={value + index}
              label={value}
              value={index}
              sx={(theme) => ({
                backgroundColor: theme.vars.palette.filter.tabBackground,
                borderRadius: "8px 8px 0 0",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: theme.vars.palette.DataGrid.headerBg,
                },
                "&.Mui-selected": {
                  fontWeight: "bold",
                  backgroundColor: theme.vars.palette.DataGrid.headerBg,
                },
              })}
            />
          ))}
        </Tabs>
        <Row
          sx={{
            alignItems: "center",
          }}
        >
          <ExportAlertMenu />
          <ActionTooltip title="WHEEL_INFO">
            <IconButton
              sx={{ color: "text.accentInverted" }}
              size="small"
              onClick={() => setOpen(true)}
            >
              <SettingsIcon fontSize="inherit" />
            </IconButton>
          </ActionTooltip>

          <ResponsiveDialog
            sx={{ "& .MuiPaper-root": { maxWidth: "650px" } }}
            onClose={() => setOpen(false)}
            open={open}
            fullWidth={true}
            title={intl.formatMessage({ id: "TAB_ALERT_SHOW_KPIS_CHOICE" })}
            formId="create-filter-form"
          >
            <AlertDragDrop onClose={() => setOpen(false)} />
          </ResponsiveDialog>
          <ActionTooltip
            title={fullScreen ? "FULLSCREEN_OFF_INFO" : "FULLSCREEN_ON_INFO"}
          >
            <IconButton
              sx={{ color: "text.accentInverted" }}
              size="small"
              onClick={() => setFullScreen(!fullScreen)}
            >
              {fullScreen ? (
                <FullscreenExitIcon fontSize="inherit" />
              ) : (
                <FullscreenIcon fontSize="inherit" />
              )}
            </IconButton>
          </ActionTooltip>
          {query.data && selectedGroup && (
            <FilterGroupRow title={"Périmètre géographique et temporel"}>
              <FilterGroupList
                groups={query.data.results}
                onChange={handleSelectedGroupChange}
                onDelete={handleSelectedDelete}
                onUpdate={() => query.refetch().then()}
                selectedGroup={selectedGroup}
              />
              <FilterGroupName name={selectedGroup.name} />
              <FilterGroupCreate
                group={selectedGroup}
                onCreate={handleCreate}
                moduleSlug={moduleSlug}
              />
              <FilterGroupSave
                group={selectedGroup}
                onUpdate={(group) => saveGroup(group)}
              />
            </FilterGroupRow>
          )}
        </Row>
      </Row>
    </Paper>
  );
}
